"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"

const BILLING_UNAVAILABLE = "Billing is temporarily unavailable. Please try again in a moment."
const BILLING_MESSAGES: Record<string, string> = {
  questions_access_already_included:
    "Your account already includes question library access. Refresh the library to continue, or manage your existing subscription.",
  questions_checkout_pending:
    "Your previous checkout is still awaiting confirmation. Please wait before trying again. If you already paid, return to the library to check your access.",
  questions_subscription_exists:
    "You already have a question subscription. Refresh this page and choose Manage question subscription to check billing or cancel future renewals.",
  questions_subscription_not_found:
    "We couldn't find a question subscription for this account. Check that you're signed in with the account used to subscribe.",
  questions_billing_unavailable: BILLING_UNAVAILABLE,
  questions_product_misconfigured: BILLING_UNAVAILABLE,
  questions_product_not_renewing: BILLING_UNAVAILABLE,
  questions_product_conflicts_with_app: BILLING_UNAVAILABLE,
  rate_limited: "Too many billing requests. Please wait a few minutes and try again.",
  billing_impersonation_forbidden: "Exit account impersonation before opening billing.",
  email_required: "Add an email address to your account before opening billing.",
  invalid_origin: "Please refresh this page and try again.",
}

export function QuestionSubscriptionButton({ manage = false }: { manage?: boolean }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  async function proceed() {
    setBusy(true)
    setError("")
    try {
      const response = await fetch(
        `/api/interview-questions/subscription/${manage ? "portal" : "checkout"}`,
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        }
      )
      if (response.status === 401) {
        window.location.assign("/signin?next=" + encodeURIComponent("/pricing?tab=interview-questions"))
        return
      }
      const body = await response.json()
      if (!response.ok) {
        setError(
          typeof body.error === "string" && Object.hasOwn(BILLING_MESSAGES, body.error)
            ? BILLING_MESSAGES[body.error]
            : BILLING_UNAVAILABLE
        )
        setBusy(false)
        return
      }
      const target = manage ? body.portalUrl : body.checkoutUrl
      if (typeof target !== "string" || !target.startsWith("https://"))
        throw new Error("Billing is temporarily unavailable. Please try again.")
      window.location.assign(target)
    } catch {
      setError(BILLING_UNAVAILABLE)
      setBusy(false)
    }
  }
  return (
    <div>
      <Button
        type="button"
        onClick={proceed}
        disabled={busy}
        variant={manage ? "outline" : "default"}
        className="w-full text-sm font-semibold transition-all duration-300 hover:scale-[1.02]"
      >
        {busy
          ? "Opening secure billing…"
          : manage
            ? "Manage question subscription"
            : "Subscribe"}
      </Button>
      {error ? (
        <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : null}
    </div>
  )
}
